import { type FC } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';

/**
 * Props for the LoadingSpinner component
 */
interface LoadingSpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'primary' | 'accent' | 'surface' | 'white';
  /** Additional CSS classes */
  className?: string;
  /** Label for accessibility */
  label?: string;
}

/**
 * LoadingSpinner Component
 *
 * An elegant loading indicator inspired by Arabic calligraphic flourishes.
 * Features a smooth, flowing animation that feels organic and refined.
 *
 * Features:
 * - Three sizes for different contexts
 * - Multiple color variants
 * - Accessible with sr-only label
 * - Reduced motion support
 */
export const LoadingSpinner: FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  className,
  label = 'Loading',
}) => {
  // Size configurations
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  const strokeWidths = {
    sm: 3,
    md: 2.5,
    lg: 2,
  };

  // Color configurations
  const colorClasses = {
    primary: 'text-primary-600',
    accent: 'text-accent-500',
    surface: 'text-surface-500',
    white: 'text-white',
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center justify-center',
        className
      )}
    >
      <svg
        className={cn(
          'animate-spin',
          sizeClasses[size],
          colorClasses[variant]
        )}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Background circle - subtle track */}
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth={strokeWidths[size]}
          strokeOpacity="0.2"
        />

        {/* Animated arc - calligraphy-inspired flowing stroke */}
        <path
          d="M12 2C6.477 2 2 6.477 2 12"
          stroke="currentColor"
          strokeWidth={strokeWidths[size]}
          strokeLinecap="round"
        />
      </svg>

      {/* Screen reader only label */}
      <span className="sr-only">{label}</span>
    </div>
  );
};

/**
 * Full page loading spinner with centered positioning
 */
export const PageLoadingSpinner: FC<Omit<LoadingSpinnerProps, 'size'>> = ({
  variant = 'primary',
  className,
  label,
}) => {
  return (
    <div
      className={cn(
        'flex items-center justify-center',
        'min-h-[200px] w-full',
        className
      )}
    >
      <LoadingSpinner size="lg" variant={variant} label={label} />
    </div>
  );
};

/**
 * Overlay loading spinner for blocking operations
 */
export const OverlayLoadingSpinner: FC<Omit<LoadingSpinnerProps, 'size'>> = ({
  variant = 'primary',
  className,
  label,
}) => {
  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-modal',
        'flex items-center justify-center',
        'bg-surface-900/20 backdrop-blur-sm',
        className
      )}
    >
      <div
        className={cn(
          'flex flex-col items-center gap-4',
          'p-6 bg-white rounded-2xl shadow-soft-xl',
          'animate-scale-in'
        )}
      >
        <LoadingSpinner size="lg" variant={variant} label={label} />
        {label && (
          <span className="text-surface-600 text-sm font-medium">
            {label}
          </span>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LoadingSpinner;
